/**
 * @file Indique si un jour appartient à la liste d'attente de lancement d'un nouveau restaurant.
 * @module packages.common.utils.restaurant.preOpening
 *
 * Miroir côté widget de `restaurants.utils.preOpening` (backend), qui reste seul
 * juge de ce qui est réservable : le widget s'en sert uniquement pour adapter ses
 * textes. Pendant le lancement, le service n'est pas « complet », le restaurant
 * prend simplement les inscriptions avant d'ouvrir ses réservations. Les textes
 * automatiques de l'encart d'ouverture sont calculés ici, une seule fois, pour le
 * widget comme pour les exemples affichés au panel admin.
 */

import {addDays, format, isValid, parseISO} from 'date-fns';

/** Longueur maximale du titre et du détail personnalisés, par langue (alignée sur le serveur). */
export const PRE_OPENING_TEXT_MAX_LENGTH = 500;

/** Jour en toutes lettres, en milieu de phrase, au même format que le bandeau des prochaines disponibilités. */
const DAY_IN_SENTENCE_FORMAT = 'EEEE d MMMM';

/**
 * Indique si un jour précède l'ouverture d'un nouveau restaurant.
 *
 * Source unique côté widget de la règle « avant l'ouverture » : calendrier (jours
 * grisés), mois affiché à l'arrivée et titre automatique de l'encart d'ouverture.
 *
 * @param {string|null} openingDate - Jour d'ouverture 'yyyy-MM-dd'
 * @param {Date|null} date - Jour à tester
 * @return {boolean} Vrai si le jour est antérieur au jour d'ouverture
 */
export function isBeforeOpeningDay(openingDate, date) {
  if (!openingDate || !date || !isValid(date)) return false;
  return format(date, 'yyyy-MM-dd') < openingDate;
}

/**
 * Indique si un jour est dans la période de liste d'attente de lancement.
 *
 * @param {Object|null} preOpening - Réglage actif exposé par la config publique `{openingDate, waitlistUntil}`
 * @param {Date|null} date - Jour choisi par le client
 * @return {boolean} Vrai entre la date d'ouverture et la fin de la liste d'attente incluses
 */
export function isLaunchWaitlistDay(preOpening, date) {
  if (!preOpening?.openingDate || !preOpening.waitlistUntil || !date || !isValid(date)) return false;
  const dayKey = format(date, 'yyyy-MM-dd');
  return dayKey >= preOpening.openingDate && dayKey <= preOpening.waitlistUntil;
}

/**
 * Textes automatiques de l'encart d'ouverture, remplacés un à un par les textes personnalisés.
 *
 * Avant l'ouverture, le titre annonce la date ; une fois ouvert (période de
 * lancement en cours), il dit que le restaurant vient d'ouvrir. Le détail
 * annonce la période de liste d'attente et le premier jour réservable, ou invite
 * à réserver quand il n'y a pas de période de lancement.
 *
 * @param {Object} params
 * @param {Object} params.preOpening - Réglage `{openingDate, waitlistUntil}` (jours 'yyyy-MM-dd')
 * @param {string} [params.restaurantName] - Nom de l'établissement
 * @param {Function} params.t - Fonction de traduction (clés `widgets.reservation.preOpening.*`)
 * @param {Object} [params.formatOptions] - Options date-fns (locale)
 * @param {Date} params.today - Jour courant
 * @return {{title: string, detail: string}} Titre et détail automatiques
 */
export function buildOpeningDefaultTexts({preOpening, restaurantName, t, formatOptions, today}) {
  const dayLabel = (date) => format(date, DAY_IN_SENTENCE_FORMAT, formatOptions);
  const nameSuffix = restaurantName ? '' : 'NoName';
  const title = isBeforeOpeningDay(preOpening.openingDate, today) ?
    t(`widgets.reservation.preOpening.opensOn${nameSuffix}`, {name: restaurantName, date: dayLabel(parseISO(preOpening.openingDate))}) :
    t(`widgets.reservation.preOpening.openedTitle${nameSuffix}`, {name: restaurantName});
  const detail = preOpening.waitlistUntil ?
    t('widgets.reservation.preOpening.waitlistPeriod', {
      until: dayLabel(parseISO(preOpening.waitlistUntil)),
      from: dayLabel(addDays(parseISO(preOpening.waitlistUntil), 1)),
    }) :
    t('widgets.reservation.preOpening.reserveNow');
  return {title, detail};
}
